<template>
	<div class="app">
		<AppHeader fixed>
			<SidebarToggler class="d-lg-none" display="md" mobile />
			<b-link class="navbar-brand" to="/dashboard">
				<img class="navbar-brand-full" src="pallet-pooling-logo-with-text.svg" width="90" height="43"
					alt="ARES Logo" />
				<img class="navbar-brand-minimized" src="img/logo-sygnet.png" width="32" height="32" alt="ARES Logo" />
			</b-link>
			<SidebarToggler class="d-md-down-none" display="lg" />
			<b-navbar-nav class="ml-auto">
				<b-img src="img/npc_seal.png" alt="NPC Seal" height="40" @click="showModal = true" />
				<!-- Show NPC Seal Modal -->
				<b-modal v-model="showModal" @shown="modalReady = true" @hide="modalReady = false" hide-header
					hide-footer centered size="s" body-class="d-flex justify-content-center align-items-center p-0"
					content-class="bg-transparent border-0">
					<transition name="popup">
						<b-img v-if="modalReady" src="img/npc_seal.png" fluid alt="Zoomed NPC Seal"
							class="rounded cursor-pointer" style="max-height: 90vh;" @click="showModal = false" />
					</transition>
				</b-modal>
				<Notifications />
				<DefaultHeaderDropdownAccnt :username="username" :userCompany="userCompany" :userType="userType" />
			</b-navbar-nav>
		</AppHeader>
		<div class="app-body">
			<AppSidebar fixed class="sidenav">
				<SidebarHeader />
				<SidebarForm />
				<SidebarNav :navItems="nav"></SidebarNav>
				<SidebarFooter />
				<SidebarMinimizer />
			</AppSidebar>
			<main class="main">
				<div class="container-fluid">
					<router-view></router-view>
				</div>
			</main>
		</div>
		<TheFooter class="footer">
			<!--footer-->
			<div class="ml-auto">
				<a href="https://www.ayun.ph" class="footer-text">Pallet Pooling</a>
				<span class="ml-1">&copy; {{ currentYear }}
					<img src="img/tawitech_logo_white.png" width="auto" height="35" alt="TAWItech, Inc." />
				</span>
			</div>
		</TheFooter>

		<!-- Modals -->
		<UpdateCredentials />
	</div>
</template>

<script>
import managerNav from '@/containers/menu/_managerNav';
import scannerNav from '@/containers/menu/_scannerNav';
import retailerNav from '@/containers/menu/_retailerNav';
import {
	Header as AppHeader,
	SidebarToggler,
	Sidebar as AppSidebar,
	SidebarFooter,
	SidebarForm,
	SidebarHeader,
	SidebarMinimizer,
	SidebarNav,
	Aside as AppAside,
	AsideToggler,
	Footer as TheFooter,
	Breadcrumb,
} from '@coreui/vue';
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt';
import Notifications from '@/views/commons/notification/Notifications';
import UpdateCredentials from '@/views/account/UpdateCredentials';
import _ from 'lodash';

export default {
	name: 'full',
	components: {
		AsideToggler,
		AppHeader,
		AppSidebar,
		AppAside,
		TheFooter,
		Breadcrumb,
		DefaultHeaderDropdownAccnt,
		SidebarForm,
		SidebarFooter,
		SidebarToggler,
		SidebarHeader,
		SidebarNav,
		SidebarMinimizer,
		Notifications,
		UpdateCredentials,
	},
	data() {
		return {
			nav: [],

			isManager: this.$store.getters.isManager,
			isScanner: this.$store.getters.isScanner,
			isRetailer: this.$store.getters.isRetailer,
			loggedUser: this.$store.getters.loggedUser,
			loggedUserCompany: this.$store.getters.loggedUserCompany,
			showModal: false,
			modalReady: false,
		};
	},
	created() {
		if (this.isScanner) {
			this.nav = scannerNav.items;
		} else if (this.isRetailer) {
			this.nav = retailerNav.items;
		} else {
			this.nav = managerNav.items;
		}
	},
	computed: {
		currentYear() {
			return new Date().getFullYear();
		},
		name() {
			return this.$route.name;
		},
		list() {
			return this.$route.matched.filter(
				(route) => route.name || route.meta.label
			);
		},
		username() {
			if (this.$store.getters.email) {
				return this.$store.getters.email;
			}
			return '';
		},
		userCompany() {
			if (this.$store.getters.email) {
				return this.$store.getters.loggedUser.company;
			}
			return '';
		},
		userType() {
			if (this.$store.getters.email) {
				return this.$store.getters.loggedUser.type;
			}
			return '';
		},
		isNewUser() {
			const currUser = this.$store.getters.loggedUser;
			if (!_.isEmpty(currUser)) {
				return currUser.isNewUser;
			}
			return false;
		}
	},
	async mounted() {
		// if a superadmin user trying to access a non-admin page return to admin dashboard
		if (this.$store.getters.isSuperAdmin) {
			this.$router.push('/admin/dashboard');
			this.$toaster.warning('You are not allowed to access this page.');
		} else if (this.isNewUser) {
			this.$bvModal.show('update-credentials');
		}

		this.nav = await this.filterNavigations();
	},
	methods: {
		async filterNavigations() {
			let filteredNav = this.nav;
			let permissions = this.loggedUserCompany.permissions ? this.loggedUserCompany.permissions : {};

			if (_.isEmpty(permissions)) {
				this.$toaster.warning('You are not allowed to access this page.');
				await this.$store.dispatch('logout');

				let previousState = this.$store.getters.previousState;
				if (!_.isEmpty(previousState)) {
					this.$store.replaceState(previousState);
				}

				this.$router.push('/login');
				return;
			}

			if (!permissions.cms) {
				filteredNav = this.removeAccountSetupAccess(filteredNav);
			}

			if (!permissions.dispatch && !permissions.transport) {
				// Account Setup
				this.removePageAccess(filteredNav, 'Account Setup', 'Transportation');

				// Reports
				this.removePageAccess(filteredNav, 'Reports', 'Dispatch Alert Logs');
				this.removePageAccess(filteredNav, 'Reports', 'Incident Report');
			}

			if (!permissions.dispatch) {
				// Account Setup
				this.removePageAccess(filteredNav, 'Account Setup', 'Connection');

				// Transactions
				this.removePageAccess(filteredNav, 'Transactions', 'Asset Pool Distribution');
				this.removePageAccess(filteredNav, 'Transactions', 'Dispatch');
				this.removePageAccess(filteredNav, 'Transactions', 'Receipt');

				// Reports
				this.removePageAccess(filteredNav, 'Reports', 'Asset Accountability');
				this.removePageAccess(filteredNav, 'Reports', 'Assets with Truckers');
				this.removePageAccess(filteredNav, 'Reports', 'Discrepancy Report');
				this.removePageAccess(filteredNav, 'Reports', 'Delayed Transfer Report');
			}

			if (!permissions.inventory) {
				// Transactions
				this.removePageAccess(filteredNav, 'Transactions', 'Inventory Session');
			}

			if (!permissions.inventory && !permissions.dispatch) {
				// Reports
				this.removePageAccess(filteredNav, 'Reports', 'Inventory Summary');
			}

			if (!permissions.billing) {
				// Billing
				this.removePageAccess(filteredNav, 'Billing', 'Client Account');
				this.removePageAccess(filteredNav, 'Billing', 'Billing Report');
			}

			if (!permissions.maintenance) {
				// Maintenance
				this.removePageAccess(filteredNav, 'Transactions', 'Maintenance');
			}

			if (!permissions.transport) {
				// Reports
				this.removePageAccess(filteredNav, 'Reports', 'Dispatch Alert Logs');
				this.removePageAccess(filteredNav, 'Reports', 'Incident Report');
			}

			if (!this.isManager) {
				// Support
				this.removePageAccess(filteredNav, 'Support', 'User Session Logs');
			}

			return filteredNav;
		},

		removeAccountSetupAccess(filteredNav) {
			// Account Setup
			this.removePageAccess(filteredNav, 'Account Setup', 'Company');
			this.removePageAccess(filteredNav, 'Account Setup', 'Storage Location');
			this.removePageAccess(filteredNav, 'Account Setup', 'User');
			this.removePageAccess(filteredNav, 'Account Setup', 'Connection');
			this.removePageAccess(filteredNav, 'Account Setup', 'Transportation');
			this.removePageAccess(filteredNav, 'Account Setup', 'Asset Type');

			return filteredNav;
		},

		removePageAccess(filteredNav, sectionTitle, pageTitle) {
			let index = _.findIndex(filteredNav, (o) => {
				return o.name === sectionTitle;
			});

			let selNav = filteredNav[index];
			if (selNav) {
				// Remove Page from the Section
				selNav.children = _.filter(selNav.children, (o) => {
					return o.name !== pageTitle;
				});

				// Remove Section for empty page options
				if (_.isEmpty(selNav.children)) {
					_.remove(filteredNav, (o) => {
						return o.name === sectionTitle;
					});
				}
			}
		},
	},
};
</script>
<style>
.list {
	color: #024F98;
}

.popup-enter-active,
.popup-leave-active {
	animation-duration: 0.3s;
	animation-fill-mode: both;
}

.popup-enter-active {
	animation-name: popupZoomIn;
}

.popup-leave-active {
	animation-name: popupZoomOut;
}

@keyframes popupZoomIn {
	from {
		transform: scale(0.8);
		opacity: 0;
	}

	to {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes popupZoomOut {
	from {
		transform: scale(1);
		opacity: 1;
	}

	to {
		transform: scale(0.8);
		opacity: 0;
	}
}
</style>
